import { z } from 'zod';
import {
  ShiftbookEntryPrioritySchema,
  ShiftbookEntryTemplateResolutionSchema,
  ShiftbookEntryTypeSchema,
  ShiftbookEntryWorkflowStatusSchema,
  ShiftbookKeywordSelectionSchema,
  ShiftbookResponsiblePartyReferenceSchema,
} from '@blms/shared/schemas/shiftbook-entry';

const StagedCommentDraftSchema = z.object({
  clientId: z.string(),
  body: z.string(),
  createdAt: z.string(),
});

const StagedAttachmentAddDraftSchema = z.object({
  clientId: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  createdAt: z.string(),
});

const StagedChecklistAddDraftSchema = z.object({
  clientId: z.string(),
  text: z.string(),
  isDone: z.boolean(),
  createdAt: z.string(),
});

const StagedChecklistUpdateDraftSchema = z.object({
  id: z.string(),
  text: z.string().optional(),
  isDone: z.boolean().optional(),
});

const ChecklistOrderRefSchema = z.discriminatedUnion('ref', [
  z.object({ ref: z.literal('id'), id: z.string() }),
  z.object({ ref: z.literal('clientId'), clientId: z.string() }),
]);

export const EntryWorkspaceDraftSchema = z.object({
  mode: z.enum(['create', 'update']),
  baseVersion: z.number().optional(),
  site: z.string(),
  type: ShiftbookEntryTypeSchema,
  status: ShiftbookEntryWorkflowStatusSchema,
  flocId: z.string(),
  templateRevisionId: z.string(),
  displayHeading: z.string(),
  templateResolution: ShiftbookEntryTemplateResolutionSchema,
  description: z.string(),
  priority: ShiftbookEntryPrioritySchema,
  responsible: ShiftbookResponsiblePartyReferenceSchema.nullable(),
  dueAt: z.string(),
  suggestionKapTagId: z.string().default(''),
  suggestionKapTagNumber: z.string().default(''),
  suggestionKapTagDescription: z.string().default(''),
  keywords: z.array(ShiftbookKeywordSelectionSchema).default([]),
  stagedComments: z.array(StagedCommentDraftSchema),
  stagedAttachmentAdds: z.array(StagedAttachmentAddDraftSchema),
  stagedAttachmentDeletes: z.array(z.string()),
  stagedChecklistAdds: z.array(StagedChecklistAddDraftSchema).default([]),
  stagedChecklistUpdates: z.array(StagedChecklistUpdateDraftSchema).default([]),
  stagedChecklistDeletes: z.array(z.string()).default([]),
  stagedChecklistOrder: z.array(ChecklistOrderRefSchema).nullable().default(null),
});

export type PersistedEntryWorkspaceDraft = z.infer<typeof EntryWorkspaceDraftSchema>;
